import { SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';
import {
  IIndirectTaxListRegistrationsV2Response,
  IIndirectTaxRegistrationV2Response,
  IIndirectTaxRegistrationsFilters,
  IUseAllIndirectTaxRegistrationsParams,
  IUseAllIndirectTaxRegistrationsResult,
} from './types';

const PAGE_LIMIT = 100;

/**
 * Fetches every active registration for a corporation across all pages.
 *
 * Use this in places that need a complete view of the corp's registrations
 * (e.g. the exposure dashboard's coverage detection, where missing a single
 * registration causes a row to falsely show "Exposed" instead of "Remitting").
 *
 * For paginated UI surfaces (registrations dashboard, wizards), use the
 * single-page `useIndirectTaxRegistrations` hook instead — it preserves the
 * cursor/limit semantics that drive the table's pagination controls.
 *
 * Implementation: a single SWR cache key over the filter set; the fetcher
 * walks the API's cursor internally until no more pages exist, then returns
 * the merged array. One SWR entry, automatic dedupe across components.
 */
export function useAllIndirectTaxRegistrations({
  filters,
}: IUseAllIndirectTaxRegistrationsParams): IUseAllIndirectTaxRegistrationsResult {
  const buildQueryString = (f: IIndirectTaxRegistrationsFilters): string => {
    const query = new URLSearchParams();
    if (f.corporationId) {
      query.append('corporationId', f.corporationId.toString());
    }
    f.countries?.forEach((c) => query.append('countries', c));
    f.states?.forEach((s) => query.append('states', s));
    f.jurisdictionTypes?.forEach((t) => query.append('jurisdictionTypes', t));
    if (f.jurisdictionName)
      query.append('jurisdictionName', f.jurisdictionName);
    if (f.registrationStatus) {
      query.append('registrationStatus', f.registrationStatus);
    }
    if (f.validationStatus) {
      query.append('validationStatus', f.validationStatus);
    }
    if (f.includeArchived !== undefined) {
      query.append('includeArchived', f.includeArchived.toString());
    }
    query.append('limit', PAGE_LIMIT.toString());
    return query.toString();
  };

  const baseQuery = buildQueryString(filters);

  // Gate the hook when the corp id isn't ready — SWR with a null key skips
  // the fetch, matching the standard "disable hooks when inputs aren't ready".
  const swrKey = filters.corporationId
    ? `new-sales-tax/registrations-v2/all?${baseQuery}`
    : null;

  const { data, mutate, error, isLoading } = useSWR<
    IIndirectTaxRegistrationV2Response[]
  >(
    swrKey,
    async () => {
      const all: IIndirectTaxRegistrationV2Response[] = [];
      let cursor = '';
      do {
        const url = cursor
          ? `new-sales-tax/registrations-v2/list?${baseQuery}&cursor=${encodeURIComponent(cursor)}`
          : `new-sales-tax/registrations-v2/list?${baseQuery}`;
        const page = (await SWRFetcher({ method: 'GET' })(
          url
        )) as IIndirectTaxListRegistrationsV2Response;
        all.push(...(page.registrations ?? []));
        cursor = page.cursor ?? '';
      } while (cursor);
      return all;
    },
    { keepPreviousData: true }
  );

  return {
    data,
    total: data?.length,
    mutate,
    error,
    isLoading,
  };
}
